/**
 * 🌾 GrainHouse + 💎 Chits Mines — two sub-tabs mounted inside FarmPage.
 *
 * - GrainHouse (v2, Feb 2026): safe Chits vault, protects against PvP nest raids.
 *   Lv 1-20. Cap ladder 2 → 8,333 Chits (owner-spec'd ladder).
 *   Upgrade cost ladder 1 → 47 Chits.
 *   Manual deposit/withdraw between overflow ⇄ safe.
 *   `grandfathered` is the soft-preserved migration excess (withdraw-only, no new deposits).
 *
 * - Mines: passive Chits income generator.
 *   1 mine, 5 vein slots. Pet Lv gates active slot count.
 *   Each vein levels independently 1→10. Lazy-settled on every state-read.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ui/confirm-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ───────── GrainHouse ───────── */
export function GrainHousePanel({ refreshParent }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [depositAmt, setDepositAmt] = useState("");
  const [withdrawAmt, setWithdrawAmt] = useState("");

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/grainhouse/state`);
      setState(r.data);
    } catch (e) {
      toast.error("Failed to load GrainHouse.");
    }
  };

  useEffect(() => { refresh(); }, []);

  const onDeposit = async () => {
    const amount = parseInt(depositAmt, 10);
    if (!amount || amount <= 0) { toast.error("Enter a positive amount."); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/grainhouse/deposit`, { amount });
      toast.success(`🌾 Deposited ${(r.data.moved || amount).toLocaleString()} 🪙 to safety.`);
      setDepositAmt("");
      refresh();
      refreshParent && refreshParent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deposit failed.");
    } finally {
      setBusy(false);
    }
  };

  const onWithdraw = async () => {
    const amount = parseInt(withdrawAmt, 10);
    if (!amount || amount <= 0) { toast.error("Enter a positive amount."); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/grainhouse/withdraw`, { amount });
      toast.success(`📤 Withdrew ${(r.data.moved || amount).toLocaleString()} 🪙 to overflow.`);
      setWithdrawAmt("");
      refresh();
      refreshParent && refreshParent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Withdraw failed.");
    } finally {
      setBusy(false);
    }
  };

  const onUpgrade = async () => {
    const ok = await confirmDialog({
      title: "Upgrade GrainHouse?",
      message: `Cost: ${(state?.upgrade_cost_chits || 0).toLocaleString()} 🪙 Chits\nLv ${state.level} → Lv ${state.next_level} · cap ${state.cap.toLocaleString()} → ${(state.next_cap || 0).toLocaleString()}`,
      confirmLabel: "Upgrade",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/grainhouse/upgrade`);
      toast.success(`🆙 GrainHouse → Lv ${r.data.new_level} · cap ${r.data.new_cap.toLocaleString()} 🪙`);
      refresh();
      refreshParent && refreshParent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upgrade failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!state) return <div className="text-neutral-500 text-sm">Loading GrainHouse…</div>;

  const pct = Math.min(100, Math.round((state.safe / Math.max(1, state.cap)) * 100));

  return (
    <div className="space-y-5" data-testid="grainhouse-panel">
      {/* Hero card with capacity */}
      <div className="rounded-xl border border-amber-700/40 bg-gradient-to-br from-[#1a0f00] via-black/60 to-[#0d0a02] p-5 relative overflow-hidden">
        <div className="absolute top-0 right-0 px-3 py-1 rounded-bl-xl bg-amber-500/15 text-amber-300 text-[10px] font-black tracking-wider">
          🛡️ SAFE FROM RAIDS
        </div>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-3xl font-black text-amber-200" style={{ fontFamily: 'Anton, sans-serif' }}>
              🌾 GRAINHOUSE — Lv {state.level} <span className="text-neutral-500 text-base font-normal">/ {state.max_level}</span>
            </div>
            <div className="text-xs text-neutral-400 mt-1">
              Safe vault for your 🪙 Chits — raid-proof. Farm Chits-seed harvests deposit here first.
            </div>
          </div>
        </div>

        {/* Capacity bar */}
        <div className="mt-4">
          <div className="flex justify-between text-xs text-neutral-400 mb-1">
            <span>Stored</span>
            <span data-testid="gh-cap-label">{state.safe.toLocaleString()} / {state.cap.toLocaleString()} 🪙</span>
          </div>
          <div className="h-4 rounded-full bg-neutral-900 border border-neutral-700 overflow-hidden relative">
            <div className={`h-full transition-all duration-300 ${state.is_full ? "bg-gradient-to-r from-red-500 to-amber-400" : "bg-gradient-to-r from-amber-500 to-amber-300"}`}
                 style={{ width: `${pct}%` }} data-testid="gh-cap-bar" />
            <div className="absolute inset-0 flex items-center justify-center text-[10px] font-black text-black mix-blend-difference">
              {pct}%
            </div>
          </div>
          {state.is_full && (
            <div className="mt-2 text-xs text-red-300" data-testid="gh-full-warning">
              ⚠️ FULL — Chits-seed harvests are BLOCKED until you upgrade or withdraw.
            </div>
          )}
        </div>

        {/* Wallets */}
        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg border border-amber-700/40 bg-black/40 p-3">
            <div className="text-[10px] uppercase tracking-wider text-amber-400/70">Safe</div>
            <div className="text-xl font-black text-amber-200" data-testid="gh-safe-val">{state.safe.toLocaleString()} 🪙</div>
          </div>
          <div className="rounded-lg border border-red-700/40 bg-black/40 p-3">
            <div className="text-[10px] uppercase tracking-wider text-red-400/70">Overflow · RAIDABLE</div>
            <div className="text-xl font-black text-red-300" data-testid="gh-overflow-val">{state.overflow.toLocaleString()} 🪙</div>
          </div>
        </div>

        {/* 🛡️ Grandfathered chip — only visible if user has migration excess */}
        {state.grandfathered > 0 && (
          <div className="mt-3 rounded-lg border border-purple-600/50 bg-purple-950/40 p-3 flex items-center justify-between gap-3 flex-wrap"
               data-testid="gh-grandfathered">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-purple-300/80">🛡️ Grandfathered (v1 migration)</div>
              <div className="text-lg font-black text-purple-100">{state.grandfathered.toLocaleString()} 🪙</div>
              <div className="text-[10px] text-purple-300/70 mt-0.5">Withdraw-only. Drains first when you withdraw. Cannot accept new deposits.</div>
            </div>
            <button onClick={() => setWithdrawAmt(String(state.grandfathered))}
                    className="px-3 py-1.5 rounded bg-purple-700 hover:bg-purple-600 text-white text-xs font-bold"
                    data-testid="gh-grandfathered-claim">
              Claim All →
            </button>
          </div>
        )}
      </div>

      {/* Deposit / Withdraw */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-neutral-800 bg-black/40 p-4">
          <div className="font-bold text-amber-200 mb-1">📥 Deposit · Overflow → Safe</div>
          <div className="text-xs text-neutral-500 mb-3">Move raidable Chits into the vault. Free, instant.</div>
          <div className="flex gap-2">
            <input type="number" min={1} value={depositAmt}
                   onChange={(e) => setDepositAmt(e.target.value)}
                   placeholder="Amount"
                   data-testid="gh-deposit-input"
                   className="flex-1 px-3 py-2 rounded bg-black/60 border border-neutral-700 text-sm" />
            <button data-testid="gh-deposit-btn"
                    disabled={busy || state.is_full}
                    onClick={onDeposit}
                    className="px-4 py-2 rounded bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-bold text-sm">
              Deposit
            </button>
          </div>
          <button onClick={() => setDepositAmt(String(state.overflow))}
                  data-testid="gh-deposit-max"
                  className="mt-2 text-xs text-amber-400/80 hover:text-amber-300 underline">
            Max ({state.overflow.toLocaleString()})
          </button>
        </div>

        <div className="rounded-xl border border-neutral-800 bg-black/40 p-4">
          <div className="font-bold text-amber-200 mb-1">📤 Withdraw · Safe → Overflow</div>
          <div className="text-xs text-neutral-500 mb-3">Pull from the vault. Spending only works from overflow.</div>
          <div className="flex gap-2">
            <input type="number" min={1} value={withdrawAmt}
                   onChange={(e) => setWithdrawAmt(e.target.value)}
                   placeholder="Amount"
                   data-testid="gh-withdraw-input"
                   className="flex-1 px-3 py-2 rounded bg-black/60 border border-neutral-700 text-sm" />
            <button data-testid="gh-withdraw-btn"
                    disabled={busy}
                    onClick={onWithdraw}
                    className="px-4 py-2 rounded bg-neutral-700 hover:bg-neutral-600 disabled:opacity-50 text-amber-200 font-bold text-sm">
              Withdraw
            </button>
          </div>
          <button onClick={() => setWithdrawAmt(String(state.safe))}
                  data-testid="gh-withdraw-max"
                  className="mt-2 text-xs text-amber-400/80 hover:text-amber-300 underline">
            Max ({state.safe.toLocaleString()})
          </button>
        </div>
      </div>

      {/* Upgrade */}
      <div className="rounded-xl border border-amber-600/40 bg-black/50 p-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-bold text-amber-200">🆙 Upgrade GrainHouse</div>
            <div className="text-xs text-neutral-500">
              {state.next_level
                ? `Lv ${state.level} → Lv ${state.next_level} · cap ${state.cap.toLocaleString()} → ${state.next_cap.toLocaleString()} 🪙`
                : `Already at MAX (Lv ${state.max_level})`}
            </div>
          </div>
          {state.upgrade_cost_chits !== null && (
            <button data-testid="gh-upgrade-btn"
                    disabled={busy || (state.overflow + state.safe) < state.upgrade_cost_chits}
                    onClick={onUpgrade}
                    className="px-4 py-2 rounded bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold text-sm">
              🪙 {state.upgrade_cost_chits.toLocaleString()}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


/* ───────── Chits Mines ───────── */

function _toastAchievements(list) {
  if (!Array.isArray(list)) return;
  list.forEach((a) => {
    const reward = a.reward_chits_granted ? ` · +${a.reward_chits_granted.toLocaleString()} 🪙` : "";
    toast.success(`${a.icon} ACHIEVEMENT UNLOCKED — ${a.label}${reward}`, {
      description: a.desc,
      duration: 6000,
    });
  });
}


export function MinesPanel({ refreshParent }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/mines/state`);
      setState(r.data);
    } catch (e) {
      toast.error("Failed to load Mines.");
    }
  };

  useEffect(() => {
    refresh();
    // Soft poll every 60s so the rate ticker stays fresh while open
    const id = setInterval(refresh, 60_000);
    return () => clearInterval(id);
  }, []);

  const onBuildVein = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/mines/build-vein`);
      toast.success(`💎 New vein built! Producing ${r.data.veins.slice(-1)[0].rate_per_hr} 🪙/hr.`);
      _toastAchievements(r.data.newly_unlocked_achievements);
      refresh();
      refreshParent && refreshParent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Build failed.");
    } finally {
      setBusy(false);
    }
  };

  const onUpgradeVein = async (idx, cost) => {
    const ok = await confirmDialog({
      title: `Upgrade Vein #${idx}?`,
      message: `Cost: ${cost.toLocaleString()} 🪙 Chits`,
      confirmLabel: "Upgrade",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/mines/upgrade-vein`, { idx });
      const v = r.data.veins.find((x) => x.idx === idx);
      toast.success(`⛏️ Vein #${idx} → Lv ${v.level} (${v.rate_per_hr} 🪙/hr)`);
      _toastAchievements(r.data.newly_unlocked_achievements);
      refresh();
      refreshParent && refreshParent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upgrade failed.");
    } finally {
      setBusy(false);
    }
  };

  const onSettle = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/mines/settle`);
      if (r.data.produced > 0) {
        const parts = [];
        if (r.data.to_grainhouse) parts.push(`+${r.data.to_grainhouse.toLocaleString()} 🌾`);
        if (r.data.to_overflow) parts.push(`+${r.data.to_overflow.toLocaleString()} 🪙`);
        toast.success(`💎 Settled ${r.data.produced.toLocaleString()} 🪙 · ${parts.join(" · ")}`);
      } else {
        toast.info("💎 No new income to settle yet.");
      }
      _toastAchievements(r.data.newly_unlocked_achievements);
      refresh();
      refreshParent && refreshParent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Settle failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!state) return <div className="text-neutral-500 text-sm">Loading Mines…</div>;

  const totalChits = state.wallet.chits + state.wallet.grainhouse_chits;
  // Render 5 vein slots — filled or locked/empty
  const slotCards = [];
  for (let idx = 1; idx <= state.max_slots; idx++) {
    const vein = state.veins.find((v) => v.idx === idx);
    const isLockedByPet = idx > state.max_active_slots;
    const requiredPetLv = (idx - 1) * 10 + 1;
    if (vein) {
      slotCards.push(
        <VeinCard key={idx} vein={vein}
                  busy={busy}
                  canAfford={totalChits >= (vein.upgrade_cost_chits || 0)}
                  onUpgrade={() => onUpgradeVein(vein.idx, vein.upgrade_cost_chits)} />
      );
    } else if (isLockedByPet) {
      slotCards.push(
        <div key={idx}
             className="rounded-xl border border-neutral-900 bg-black/40 p-4 text-center grayscale opacity-60"
             data-testid={`mine-slot-locked-${idx}`}>
          <div className="text-4xl mb-2">🔒</div>
          <div className="text-xs text-neutral-500">Vein Slot #{idx}</div>
          <div className="text-[11px] text-red-300 mt-1">Pet Lv {requiredPetLv} required</div>
        </div>
      );
    } else {
      // Buildable slot
      slotCards.push(
        <div key={idx}
             className="rounded-xl border border-dashed border-amber-700/40 bg-black/30 p-4 text-center hover:bg-amber-950/20 transition"
             data-testid={`mine-slot-empty-${idx}`}>
          <div className="text-4xl mb-2 opacity-50">⛏️</div>
          <div className="text-xs text-neutral-500">Empty Vein Slot #{idx}</div>
          <button onClick={onBuildVein}
                  disabled={busy || totalChits < state.build_vein_cost_chits}
                  data-testid={`mine-build-btn-${idx}`}
                  className="mt-3 px-3 py-1.5 rounded bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold text-xs">
            Build · {state.build_vein_cost_chits.toLocaleString()} 🪙
          </button>
        </div>
      );
    }
  }

  return (
    <div className="space-y-5" data-testid="mines-panel">
      {/* Hero */}
      <div className="rounded-xl border border-purple-900/40 bg-gradient-to-br from-[#10001a] via-black/60 to-[#0a0a0a] p-5 relative overflow-hidden">
        <div className="absolute top-0 right-0 px-3 py-1 rounded-bl-xl bg-purple-500/15 text-purple-300 text-[10px] font-black tracking-wider">
          ⛏️ PASSIVE INCOME · AUTO-DEPOSIT
        </div>
        <div className="text-3xl font-black text-purple-200" style={{ fontFamily: 'Anton, sans-serif' }}>
          💎 CHITS MINES
        </div>
        <div className="text-xs text-neutral-400 mt-1">
          Veins produce 🪙 around the clock. Income drips into 🌾 GrainHouse first, then overflow.
        </div>

        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Rate" value={`${state.rate_per_hr.toLocaleString()} 🪙/hr`}
                sub={`${state.veins.length}/${state.max_slots} veins`} testid="mines-rate" />
          <Stat label="Vein Slots" value={`${state.max_active_slots} / ${state.max_slots}`}
                sub={state.next_vein_slot_unlock
                  ? `Next slot @ Pet Lv ${state.next_vein_slot_unlock.at_pet_level}`
                  : "All slots unlocked"}
                testid="mines-slots" />
          <Stat label="Pet Lv" value={state.pet_level} sub="affects slot count" testid="mines-petlv" />
          <Stat label="Total Mined" value={state.total_mined.toLocaleString()} sub="lifetime 🪙"
                testid="mines-total" />
        </div>

        <div className="mt-4 flex gap-2">
          <button onClick={onSettle} disabled={busy}
                  data-testid="mines-settle-btn"
                  className="px-3 py-1.5 rounded bg-purple-700 hover:bg-purple-600 disabled:opacity-50 text-amber-200 font-bold text-xs">
            ⛏️ Settle Now
          </button>
          <div className="text-[11px] text-neutral-500 self-center">
            (Income also auto-settles on every Mines page load.)
          </div>
        </div>
      </div>

      {/* 🏆 Achievements strip */}
      <div className="rounded-xl border border-amber-700/40 bg-black/40 p-4" data-testid="mines-achievements">
        <div className="flex items-center justify-between mb-3">
          <div className="font-bold text-amber-200">🏆 Mining Achievements</div>
          <div className="text-xs text-neutral-400">
            <span data-testid="mines-achievements-count">{state.achievements_unlocked_count}</span>
            <span className="text-neutral-600"> / {state.achievements_total_count}</span>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
          {(state.achievements || []).map((a) => (
            <div key={a.id}
                 className={`rounded-lg border p-3 text-center transition ${
                   a.unlocked
                     ? "border-amber-500/60 bg-amber-500/10 shadow-[0_0_12px_rgba(245,158,11,0.18)]"
                     : "border-neutral-800 bg-black/40 grayscale opacity-60"
                 }`}
                 data-testid={`mines-achievement-${a.id}`}>
              <div className="text-3xl mb-1">{a.unlocked ? a.icon : "🔒"}</div>
              <div className={`text-xs font-bold ${a.unlocked ? "text-amber-200" : "text-neutral-500"}`}>
                {a.label}
              </div>
              <div className="text-[10px] text-neutral-500 mt-1 leading-tight">{a.desc}</div>
              {a.reward_chits > 0 && (
                <div className={`text-[10px] mt-1 ${a.unlocked ? "text-amber-300" : "text-neutral-600"}`}>
                  +{a.reward_chits.toLocaleString()} 🪙
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Vein grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {slotCards}
      </div>
    </div>
  );
}


function Stat({ label, value, sub, testid }) {
  return (
    <div className="rounded-lg border border-neutral-800 bg-black/40 p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="text-lg font-black text-amber-200">{value}</div>
      {sub && <div className="text-[10px] text-neutral-500 mt-0.5">{sub}</div>}
    </div>
  );
}


function VeinCard({ vein, busy, canAfford, onUpgrade }) {
  const isMax = vein.is_max;
  return (
    <div className="rounded-xl border border-purple-700/40 bg-gradient-to-br from-[#1a0a20] to-black/60 p-4"
         data-testid={`mine-vein-card-${vein.idx}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs text-neutral-400">Vein #{vein.idx}</div>
          <div className="text-2xl font-black text-purple-200">Lv {vein.level} <span className="text-neutral-600 text-sm font-normal">/ 10</span></div>
        </div>
        <div className="text-4xl">{isMax ? "💎" : "⛏️"}</div>
      </div>

      <div className="mt-3 rounded-lg bg-black/40 border border-neutral-800 p-2.5 text-center">
        <div className="text-[10px] uppercase tracking-wider text-neutral-500">Producing</div>
        <div className="text-xl font-black text-amber-300" data-testid={`mine-vein-rate-${vein.idx}`}>
          {vein.rate_per_hr.toLocaleString()} 🪙/hr
        </div>
        {!isMax && vein.next_level_rate_per_hr && (
          <div className="text-[10px] text-neutral-500 mt-0.5">
            Next Lv: {vein.next_level_rate_per_hr.toLocaleString()} 🪙/hr
          </div>
        )}
      </div>

      <div className="mt-3">
        {isMax ? (
          <div className="px-3 py-2 rounded bg-amber-500/10 border border-amber-500/30 text-center text-amber-300 text-xs font-bold"
               data-testid={`mine-vein-max-${vein.idx}`}>
            🏆 MAX LEVEL
          </div>
        ) : (
          <button onClick={onUpgrade}
                  disabled={busy || !canAfford}
                  data-testid={`mine-vein-upgrade-${vein.idx}`}
                  className="w-full px-3 py-2 rounded bg-purple-600 hover:bg-purple-500 disabled:opacity-40 disabled:cursor-not-allowed text-amber-200 font-bold text-xs">
            🆙 Upgrade · {vein.upgrade_cost_chits.toLocaleString()} 🪙
          </button>
        )}
      </div>
    </div>
  );
}
